// =============================================================================
// Les en-têtes porteurs du parcours v0.4 (DEV-2284, lot A).
//
// Ce que le courtier doit vivre : une section repliée dit SA réponse, avec ses
// valeurs, jamais un simple titre ; dérouler donne le détail, jamais une autre
// réponse. Une réserve attachée à une section repliée monte dans son en-tête.
//
// Règle (maquette v0.4, partie « Les en-têtes ») :
// - un en-tête = un libellé, un noyau (la réponse), un complément (ses valeurs)
//   et, quand il y a lieu, une pastille (une réserve ou un état) ;
// - tout est fait de valeurs et de mots servis ; un zéro n'est écrit que s'il
//   est servi ; un montant non servi s'écrit « Montant non servi » ;
// - trois formes ne se confondent jamais : repliée (un chevron, des valeurs),
//   sans objet (une ligne sans chevron qui dit pourquoi), absente (aucun
//   en-tête, quand la section n'a aucun sens dans l'état).
//
// Ce module est PUR : il ne lit que l'étude chargée (et la paire, et
// l'historique), il n'appelle rien, il ne retient rien. Ce que la page ne sait
// pas encore (journal de la démarche, lot S1 ; dernier jour mesuré, lot B) n'y
// est pas inventé.
// =============================================================================
import { lireChiffre } from "@modules/courtage/utils/optim-turpe-v2.chiffre"
import {
    aucunDepassementEtabli,
    conseilDeRepli,
    lireArbitrageServi,
} from "@modules/courtage/utils/optim-turpe-v2.arbitrage"
import {
    lirePlancherRetenu,
    montantC5,
    reserveC5EnTitre,
    type PlancherLu,
} from "@modules/courtage/utils/optim-turpe-v2.recommandation"
import { lireEchec } from "@modules/courtage/utils/optim-turpe-v2.echec"
import { lireDemande } from "@modules/courtage/utils/optim-turpe-v2.demande"
import {
    CHAMP_DATE_BAREME,
    CHAMP_MARGE_SECURITE,
    formaterValeurEntree,
    lireRegistre,
    texteValeurRetenue,
    type EntreeLue,
} from "@modules/courtage/utils/optim-turpe-v2.registre"
import { lireRaccordement } from "@modules/courtage/utils/optim-turpe-v2.bornes"
import {
    nombreEuros,
    nombreEurosDepassement,
    formatMontantSigne,
} from "@modules/courtage/utils/optim-turpe-v2.montant"
import {
    libelleMargeC5Servie,
    libelleMarge,
} from "@modules/courtage/utils/optim-turpe-v2.marge"
import {
    constatsDeLaLigne,
    noteDernierJourMesure,
} from "@modules/courtage/utils/optim-turpe-v2.cadre"
import {
    cmdpsApplicable,
    formatDateFr,
    formatDateHeureParis,
    formatNombreFr,
    formatPuissanceFr,
    getDrivingPlage,
    getUnitePuissance,
    libelleDureeFenetre,
    lireDecompositionTurpe,
    PLAGE_ORDER,
} from "@modules/courtage/utils/optim-turpe-v2.helpers"
import {
    dateDeCalcul,
    dateDuLancementAffiche,
    grouperParLancement,
    lancementEnVigueur,
    lignesDHistorique,
} from "@modules/courtage/utils/optim-turpe-v2.cycle"
import {
    lireTitreDuResultat,
    montantDeTete,
    montantEconomiePossible,
    surLaPeriode,
} from "@modules/courtage/utils/optim-turpe-v2.titre"
import {
    LIBELLE_ETAT,
    LIBELLE_SCENARIO_ARBITRAGE,
    libelleClasseHoraire,
    libelleFormuleTarifaire,
} from "@modules/courtage/utils/optim-turpe-v2.libelles"
import type {
    OptimTurpeV2Etude,
    OptimTurpeV2PlageKey,
} from "@modules/courtage/types/optim-turpe-v2"

// -----------------------------------------------------------------------------
// Les sections du parcours
// -----------------------------------------------------------------------------

/**
 * Les sections de la page, dans l'ordre du parcours. Les identifiants sont
 * ceux de la maquette (sans préfixe) : ils servent d'ancres, de clés de
 * mémoire et de cibles de `?ouvrir=`.
 */
export type SectionParcours =
    | "bandeau"
    | "cadre"
    | "contrat"
    | "avances"
    | "t1"
    | "postes"
    | "mesures"
    | "actuelle"
    | "t2"
    | "pourquoi"
    | "classes"
    | "recommandee"
    | "explication"
    | "t3"
    | "aconfirmer"
    | "remise"
    | "t4"
    | "demarche"
    | "etudes"

/** Les trois formes d'un en-tête (règle 4). */
export type FormeEntete = "repliee" | "sans_objet" | "absente"

export type Entete = {
    forme: FormeEntete
    /** Le nom court de la section ; il ne change pas d'un état à l'autre. */
    libelle: string
    /** La réponse, en une ou deux valeurs servies. */
    noyau: string | null
    /** Les valeurs qui précisent la réponse. */
    complement: string | null
    /** Une réserve ou un état attaché à la section. */
    pastille: string | null
    /** Vrai quand la pastille porte une réserve (elle passe alors à la ligne, jamais masquée). */
    pastilleReserve: boolean
}

function entete(
    libelle: string,
    noyau: string | null,
    complement: string | null = null,
    pastille: string | null = null,
    forme: FormeEntete = "repliee",
    pastilleReserve = false
): Entete {
    return {
        forme,
        libelle,
        noyau: noyau || null,
        complement: complement || null,
        pastille: pastille || null,
        pastilleReserve: pastilleReserve && Boolean(pastille),
    }
}

function absente(libelle: string): Entete {
    return entete(libelle, null, null, null, "absente")
}

// -----------------------------------------------------------------------------
// La table des codes de réserve (cible v0.3 §9.3) : où chaque réserve s'attache,
// et l'en-tête où elle monte quand la section qui porte sa valeur est repliée.
// Passée du lot E au lot A : un en-tête replié ne peut pas sortir sans porter
// sa réserve.
// -----------------------------------------------------------------------------

export type RattachementReserve = {
    /** La valeur à côté de laquelle la réserve s'écrit, section dépliée. */
    attache: string
    /** Les en-têtes où elle monte, section repliée. */
    entetes: SectionParcours[]
    /** Le mot de la pastille, quand la réserve monte en pastille. */
    pastille: string | null
    /**
     * Le nom de la valeur qu'elle touche, écrit devant elle au temps 1 (lot E,
     * partie « Quatre textes », texte 1) : le préfixe « Sous réserve » disparaît.
     */
    valeur: string
}

export const RATTACHEMENT_DES_RESERVES: Record<string, RattachementReserve> = {
    "denivele-actuel-sous-la-pointe": {
        attache: "part des dépassements, temps 1",
        entetes: ["actuelle", "postes"],
        pastille: "dépassements attendus",
        valeur: "Dépassements",
    },
    // R14 (08/10/2026) : l'adaptateur écarte cette réserve avant toute lecture
    // (`sansVerificationDeLaPuissanceActuelle`). Si elle passait quand même,
    // elle ne peindrait plus « à vérifier » : aucune pastille.
    "denivele-actuel-peu-vraisemblable": {
        attache: "part des dépassements, temps 1",
        entetes: ["actuelle", "postes"],
        pastille: null,
        valeur: "Dépassements",
    },
    "donnees-completees": {
        attache: "Avec quelle confiance, temps 1",
        entetes: ["mesures"],
        pastille: null,
        valeur: "Mesures",
    },
    "donnees-faible": {
        attache: "Avec quelle confiance, temps 1",
        entetes: ["mesures"],
        pastille: "mesures insuffisantes",
        valeur: "Mesures",
    },
    "promesse-1102-non-tenue": {
        attache: "Avec quelle confiance, temps 1",
        entetes: ["mesures"],
        pastille: "mesures incomplètes",
        valeur: "Mesures",
    },
    "classe-temporelle-sous-observee": {
        attache: "Avec quelle confiance, temps 1",
        entetes: ["mesures"],
        pastille: "poste peu mesuré",
        valeur: "Mesures",
    },
    "reactive-completee": {
        attache: "Avec quelle confiance, temps 1",
        entetes: ["mesures"],
        pastille: null,
        valeur: "Puissance réactive",
    },
    "reactive-non-publiee": {
        attache: "Avec quelle confiance, temps 1",
        entetes: ["mesures"],
        pastille: null,
        valeur: "Puissance réactive",
    },
    "cmdps-duree-sur-pas-fabriques": {
        attache: "Avec quelle confiance, temps 1",
        entetes: ["mesures"],
        pastille: null,
        valeur: "Mesures",
    },
    "fta-sensible-au-comblement": {
        attache: "carte du scénario, temps 2",
        entetes: ["t2", "recommandee"],
        pastille: "formule sensible aux jours estimés",
        valeur: "Puissances recommandées",
    },
    "denivele-recommande-sous-la-pointe": {
        attache: "carte du scénario, temps 2",
        entetes: ["t2", "recommandee"],
        pastille: "dépassements attendus",
        valeur: "Puissances recommandées",
    },
    "raccordement-ecarte": {
        attache: "ligne de l'entrée dans « Contrat actuel », cadre",
        entetes: ["cadre", "contrat"],
        pastille: "raccordement non retenu",
        valeur: "Contrat actuel",
    },
    "entree-au-defaut": {
        attache: "ligne de l'entrée dans « Contrat actuel », cadre",
        entetes: ["cadre", "contrat"],
        pastille: "à vérifier",
        valeur: "Contrat actuel",
    },
    "montant-non-servi-mois-absents": {
        attache: "à la place du montant, temps 1",
        entetes: ["bandeau"],
        pastille: null,
        valeur: "Montant",
    },
    "modification-contractuelle-recente": {
        attache: "« À savoir avant d'envoyer », Rapport et demande",
        entetes: ["t3"],
        pastille: "modification récente",
        valeur: "Demande au fournisseur",
    },
}

const VALEUR_STRUCTURE = "Puissances recommandées"
const VALEUR_STRUCTURE_CHOISIE = "Vos puissances"

/**
 * La ligne qui reçoit toute réserve de code inconnu : visible, jamais perdue.
 * Vague wording du 14/10/2026 (Q11) : « réserve » reste un mot interne.
 */
export const AUTRES_RESERVES = "Autres remarques"

/**
 * Où monte une réserve. Un code inconnu tombe dans la ligne « Autres
 * réserves » du temps 1, qui ne se replie jamais : visible, jamais perdue.
 */
export function rattachementDeReserve(
    code: string | null | undefined
): RattachementReserve {
    return (
        (code ? RATTACHEMENT_DES_RESERVES[code] : undefined) ?? {
            attache: "Autres remarques, temps 1",
            entetes: ["t1"],
            pastille: null,
            valeur: AUTRES_RESERVES,
        }
    )
}

/**
 * Les codes servis par courtage (origin/main, 01/10) qui ne montent dans aucun
 * en-tête : ils ne changent pas la table ci-dessus, ils nomment seulement la
 * valeur qu'ils touchent au temps 1. Un code absent des deux tables va dans
 * « Autres réserves ».
 */
const VALEUR_DES_AUTRES_CODES: Record<string, string> = {
    "classe-temporelle-sous-observee-sur-lannee": "Mesures",
    // DEV-2311, F6 : servis sur une étude à historique court (bêta-test 02/10).
    "donnees-reserve": "Mesures",
    "meilleure-periode": "Période",
    "collecte-interrompue-datee": "Mesures",
    "pas-natif-regimes-meles": "Mesures",
    "couverture-plafonnee-pas-natif-contredit": "Mesures",
    "periode-sans-hiver": "Période",
    "fenetre-plus-courte-quune-annee": "Période",
    "heures-creuses-non-publiees": "Contrat actuel",
    "unite-presumee": "Contrat actuel",
    "raccordement-borne-levee": "Contrat actuel",
    "raccordement-borne-appliquee": "Contrat actuel",
    "raccordement-non-utilisable": "Contrat actuel",
    "gain-par-depassements-evites": "Dépassements",
    "structure-recommandee-plus-chere": "Puissances recommandées",
    "couverture-du-pic-plus-chere": "Puissances recommandées",
    "decomposition-non-reconciliee": "Montant",
    "economie-non-calculable": "Montant",
    "montant-non-servi-saison-haute-non-mesuree": "Montant",
    // Moteur v1.6 (contrat v1.6 §6 5.2, décisions (s) et (t) du 20/10/2026).
    "periode-depuis-baisse-de-puissance": "Période",
    "puissance-baisse-recente": "Mesures",
    "puissance-souscrite-nulle-servie": "Contrat actuel",
}

/** Le nom de la valeur qu'une réserve touche ; « Autres réserves » sinon. */
export function valeurDeReserve(code: string | null | undefined): string {
    const table = code ? RATTACHEMENT_DES_RESERVES[code] : undefined
    if (table) return table.valeur
    return (code ? VALEUR_DES_AUTRES_CODES[code] : undefined) ?? AUTRES_RESERVES
}

/**
 * Une ligne de réserves au temps 1 : la valeur touchée, puis les textes
 * servis. `alertes[i]` dit si le texte `i` porte sur la recommandation ou sur
 * le montant (A4 et Q20 du 14/10/2026 : l'orange leur est réservé, le reste
 * s'écrit en gris).
 */
export type ReservesDUneValeur = {
    valeur: string
    textes: string[]
    alertes?: boolean[]
    /** `portees[i]` : la portée servie du texte `i` (`null` si non servie). */
    portees?: (string | null)[]
}

/**
 * Une réserve change-t-elle la conclusion ? Portée servie `recommandation` ou
 * `montant` : oui ; `entree` : non, elle s'écrit en gris. Une réserve sans
 * portée servie (étude ancienne) reste en alerte : la page ne la déclasse pas
 * sans savoir.
 */
export function reserveEnAlerte(portee: string | null | undefined): boolean {
    return portee !== "entree"
}

/**
 * Lot E (partie « Quatre textes », texte 1) : la ligne « Sous réserve : a · b ·
 * c » devient une ligne par valeur touchée, dans l'ordre de la page ; les
 * textes servis sont repris tels quels. Un code inconnu tombe sur la ligne
 * « Autres réserves », toujours la dernière. Aucune réserve ne se perd.
 */
export function reservesSurLeurValeur(
    reserves: readonly {
        code?: string | null
        label?: string | null
        portee?: string | null
    }[],
    { structureChoisie = false }: { structureChoisie?: boolean } = {}
): ReservesDUneValeur[] {
    // L'ordre de lecture de la page : la période, le montant et ses parts,
    // la confiance, la carte, le contrat, la demande ; « Autres » en dernier.
    const ordre = [
        "Période",
        "Montant",
        "Dépassements",
        "Mesures",
        "Puissance réactive",
        VALEUR_STRUCTURE,
        VALEUR_STRUCTURE_CHOISIE,
        "Contrat actuel",
        "Demande au fournisseur",
        AUTRES_RESERVES,
    ]
    const parValeur = new Map<string, string[]>()
    const alertesParValeur = new Map<string, boolean[]>()
    const porteesParValeur = new Map<string, (string | null)[]>()
    for (const r of reserves) {
        const texte = (r.label ?? "").trim()
        if (!texte) continue
        const nom = valeurDeReserve(r.code)
        // Le client a imposé sa structure : elle n'est pas « recommandée ».
        const valeur =
            structureChoisie && nom === VALEUR_STRUCTURE
                ? VALEUR_STRUCTURE_CHOISIE
                : nom
        parValeur.set(valeur, [...(parValeur.get(valeur) ?? []), texte])
        alertesParValeur.set(valeur, [
            ...(alertesParValeur.get(valeur) ?? []),
            reserveEnAlerte(r.portee),
        ])
        porteesParValeur.set(valeur, [
            ...(porteesParValeur.get(valeur) ?? []),
            r.portee ?? null,
        ])
    }
    return ordre
        .filter((v) => parValeur.has(v))
        .map((valeur) => ({
            valeur,
            textes: parValeur.get(valeur) ?? [],
            alertes: alertesParValeur.get(valeur) ?? [],
            portees: porteesParValeur.get(valeur) ?? [],
        }))
}

/**
 * Fait 10 : la limite de raccordement appliquée qui limite la recommandation
 * monte en pastille sur l'en-tête du temps 2 ; « Mon contrat », qui la détaille,
 * est replié dans le cadre. Appliquée sans effet, elle reste au premier geste.
 */
function pastilleDuRaccordement(
    etude: OptimTurpeV2Etude | null | undefined
): string | null {
    const r = etude ? lireRaccordement(etude) : null
    return r?.statut === "appliquee" && r.recommandationBornee
        ? "limitée par le raccordement"
        : null
}

/** Les réserves servies qui montent dans l'en-tête d'une section. */
export function reservesQuiMontent(
    etude: OptimTurpeV2Etude | null | undefined,
    section: SectionParcours
): { code: string; pastille: string | null }[] {
    return (etude?.reserves ?? [])
        .filter((r) => rattachementDeReserve(r.code).entetes.includes(section))
        .map((r) => ({
            code: r.code,
            pastille: rattachementDeReserve(r.code).pastille,
        }))
}

/** La première pastille de réserve d'une section, ou `null`. */
function pastilleDeReserve(
    etude: OptimTurpeV2Etude | null | undefined,
    section: SectionParcours
): string | null {
    return (
        reservesQuiMontent(etude, section).find((r) => r.pastille)?.pastille ??
        null
    )
}

// -----------------------------------------------------------------------------
// Écritures
// -----------------------------------------------------------------------------

function fini(valeur: unknown): number | null {
    return typeof valeur === "number" && Number.isFinite(valeur) ? valeur : null
}

/**
 * « +1 583 € HT » ; un surcoût s'écrit « −1 583 € HT » (convention de la page,
 * revue du 08/10, R1 : utils/optim-turpe-v2.montant).
 */
export function eurosSignes(valeur: number | null): string | null {
    return formatMontantSigne(valeur, "HT")
}

/** « +792 € », sans le régime, pour les compléments déjà titrés en € HT. */
function eurosCourts(valeur: number | null): string | null {
    return formatMontantSigne(valeur)
}

/** « a », « a et b », « a, b et c ». */
function enumerer(mots: string[]): string {
    if (mots.length <= 1) return mots.join("")
    return `${mots.slice(0, -1).join(", ")} et ${mots[mots.length - 1]}`
}

function pluriel(n: number, mot: string, motPluriel = `${mot}s`): string {
    return `${formatNombreFr(n, 0)} ${n > 1 ? motPluriel : mot}`
}

type Structure = {
    fta: string | null
    /** Les puissances par classe (C4, C2) ou la puissance unique (C5). */
    puissances: Partial<Record<OptimTurpeV2PlageKey, number>>
    unique: number | null
}

function lireStructure(
    s:
        | NonNullable<OptimTurpeV2Etude["actuelle"]>["structure_tarifaire"]
        | null
        | undefined
): Structure | null {
    if (!s) return null
    const fta = s.formule_tarifaire_acheminement?.fta ?? null
    const p = s.puissance_souscrite_soutirage
    const unique = fini(p?.puissance)
    const puissances: Partial<Record<OptimTurpeV2PlageKey, number>> = {}
    for (const classe of PLAGE_ORDER) {
        const v = fini(p?.denivele?.[classe])
        if (v !== null) puissances[classe] = v
    }
    if (fta === null && unique === null && Object.keys(puissances).length === 0)
        return null
    return { fta, puissances, unique }
}

/** Les valeurs distinctes d'une structure, dans l'ordre des classes : « 416 et 483 kW ». */
function puissancesDistinctes(s: Structure, unite: string): string | null {
    if (s.unique !== null) return formatPuissanceFr(s.unique, unite)
    const valeurs: number[] = []
    for (const classe of PLAGE_ORDER) {
        const v = s.puissances[classe]
        if (v !== undefined && !valeurs.includes(v)) valeurs.push(v)
    }
    if (valeurs.length === 0) return null
    const mots = valeurs.map((v) => formatNombreFr(v, 2) ?? "")
    return `${enumerer(mots)}\u00a0${unite}`
}

// -----------------------------------------------------------------------------
// Le contexte : ce que la page a chargé
// -----------------------------------------------------------------------------

export type ScenarioPaire = "sans" | "avec"

export type ContexteEntetes = {
    /** L'étude affichée (celle du scénario retenu quand la paire existe). */
    etude: OptimTurpeV2Etude | null
    /** Le refus du dernier lancement, quand il y en a un. */
    refus?: OptimTurpeV2Etude | null
    /** La paire, quand elle existe. */
    sans?: OptimTurpeV2Etude | null
    avec?: OptimTurpeV2Etude | null
    /** Les deux scénarios diffèrent-ils sur la part ferme ? (règle du magasin) */
    arbitragePertinent?: boolean
    /** Le scénario affiché. */
    retenu?: ScenarioPaire
    /** Le scénario conseillé (servi, sinon déduit par la page). */
    conseil?: ScenarioPaire | null
}

/** L'état du parcours : il décide de la forme des en-têtes (lot C le formalise). */
export type EtatParcours =
    | "vide"
    | "refus"
    | "economie"
    | "deja_optimale"
    | "surcout"
    | "montant_non_servi"
    | "scenario_saisi"
    | "inconnu"

export function etatDuParcours(ctx: ContexteEntetes): EtatParcours {
    if (ctx.refus) return "refus"
    const etude = ctx.etude
    if (!etude) return "vide"
    if (lireEchec(etude)) return "refus"
    const chiffre = lireChiffre(etude)
    if (chiffre.estScenarioLibre) return "scenario_saisi"
    if (chiffre.montantServi === false || chiffre.etat === "montant_non_servi")
        return "montant_non_servi"
    switch (chiffre.etat) {
        case "economie":
        case "gain_par_depassements_evites":
            return "economie"
        case "deja_optimale":
            return "deja_optimale"
        case "surcout":
            return "surcout"
        default:
            return "inconnu"
    }
}

/**
 * La source d'une marge PAR DÉFAUT (US6 CA1, §5.2, D6 (a)), lue sur le canal
 * de lancement servi à la page (lot 4g, `canal` racine) :
 * - `api` : « valeur par défaut (API) » ;
 * - `ecran` : « par défaut depuis cette page » ;
 * - canal non servi (étude antérieure au lot), marge à 0 % et segment servi
 *   C4 ou C2 : « étude lancée hors de cette page », la page proposant 5 % ;
 * - sinon (`interne`, segment non servi, ou canal non servi sur une autre
 *   marge) : « valeur par défaut », sans prêter à l'étude un canal que rien
 *   ne dit.
 */
export function sourceMargeParDefaut(
    etude: Pick<OptimTurpeV2Etude, "canal" | "compteur">,
    pct: number
): string {
    if (etude.canal === "api") return "valeur par défaut (API)"
    if (etude.canal === "ecran") return "par défaut depuis cette page"
    const canalServi = typeof etude.canal === "string" && etude.canal !== ""
    const segment = etude.compteur?.segment
    const c4OuC2 = segment === "C4" || segment === "C2"
    if (!canalServi && pct === 0 && c4OuC2)
        return "étude lancée hors de cette page"
    return "valeur par défaut"
}

/**
 * Le scénario affiché n'est pas le conseil établi (revue 2, A7) ; une étude
 * saisie n'entre jamais dans ce cas, elle se dit « Vos puissances ».
 */
export function estScenarioNonRecommande(ctx: ContexteEntetes): boolean {
    return (
        ctx.arbitragePertinent === true &&
        !!ctx.retenu &&
        !!ctx.conseil &&
        ctx.retenu !== ctx.conseil &&
        etatDuParcours(ctx) !== "scenario_saisi"
    )
}

function nomScenario(s: ScenarioPaire): string {
    return LIBELLE_SCENARIO_ARBITRAGE[s]
}

/**
 * La date du lancement affiché (revue témoin du 15/10/2026, D19) : la même
 * règle que la ligne de l'historique (`dateDuLancementAffiche`), pour qu'une
 * même étude ne porte jamais deux heures.
 */
function dateEtude(
    etude: OptimTurpeV2Etude,
    ctx?: Pick<ContexteEntetes, "sans" | "avec">
): string | null {
    const d = ctx
        ? dateDuLancementAffiche(etude, ctx.sans ?? null, ctx.avec ?? null)
        : dateDeCalcul(etude)
    const lu = d ? formatDateHeureParis(d) : null
    return lu && lu !== "—" ? lu : null
}

// -----------------------------------------------------------------------------
// Le bandeau (D3) : des valeurs servies, jamais une phrase
// -----------------------------------------------------------------------------

/**
 * Le libellé court de la réserve C5 « pic au-dessus de la souscrite » (Q22),
 * le même dans la barre collante et dans l'historique (S9, D1).
 */
export const LIBELLE_COURT_RESERVE_C5 = "Pic au-dessus de la souscrite"

export type ValeurDuBandeau = {
    terme: string
    valeur: string
    /** Valeur secondaire : elle se tait sous 640 px. */
    secondaire?: boolean
}

export function valeursDuBandeau(
    ctx: ContexteEntetes,
    pdl: string | null
): ValeurDuBandeau[] {
    const etude = ctx.refus ?? ctx.etude
    if (!etude) return []
    const valeurs: ValeurDuBandeau[] = []
    const segment = etude.compteur?.segment ?? null
    const compteur = [segment, pdl].filter(Boolean).join(" · ")
    if (compteur) {
        valeurs.push({ terme: "Compteur", valeur: compteur, secondaire: true })
    }
    const etat = etatDuParcours(ctx)
    if (etat === "refus") {
        valeurs.push({ terme: "Étude", valeur: "L'étude n'a pas abouti" })
        return valeurs
    }
    // Vague wording du 14/10/2026 (Q12 à Q16) : le bandeau reprend le titre
    // de l'état, avec le montant signé (Q14) et son unité (Q15) quand il y en
    // a un ; « Rien à changer » et « Économie non chiffrée » se disent sans
    // montant.
    const chiffre = lireChiffre(etude)
    const tete = montantDeTete(chiffre)
    const montant = tete
        ? `${eurosSignes(tete.montantEur)} ${tete.unite}`
        : null
    const scenarioNonRecommande = estScenarioNonRecommande(ctx)
    // Le nom du scénario déjà porté par le verdict de la barre (A7) ne se
    // redit pas dans « Scénario affiché ».
    let nomDejaDit = false
    if (etat === "montant_non_servi" || chiffre.netteEur === null) {
        valeurs.push({ terme: "Résultat", valeur: "Économie non chiffrée" })
    } else if (etat === "scenario_saisi") {
        // Avant le cas « scénario non recommandé », dans le même ordre que la
        // carte de tête : une étude saisie se dit « Vos puissances »
        // (relecture sceptique de la revue 2).
        valeurs.push({ terme: "Vos puissances", valeur: montant ?? "" })
    } else if (scenarioNonRecommande && montant) {
        // Revue 2 en production (I24, I61, lot A7) : le scénario affiché
        // n'est pas la recommandation ; la barre dit le même verdict que la
        // carte, « Couvrir les dépassements : −101 € HT par an ».
        valeurs.push({ terme: nomScenario(ctx.retenu!), valeur: montant })
        nomDejaDit = true
    } else if (etat === "economie") {
        valeurs.push({
            terme: chiffre.etat ? LIBELLE_ETAT[chiffre.etat] : "Montant",
            valeur: montant ?? "",
        })
    } else {
        // Revue témoin du 15/10/2026 : la barre dit le MÊME verdict que la
        // carte de tête. D3 : couvrir coûte, assumer gagne (maquette 2 bis),
        // « Économie estimée … en assumant les dépassements », jamais « Rien à
        // changer ». D1 : un C5 dont le pic dépasse la souscrite (Q22) porte
        // le libellé court de sa réserve, comme l'historique.
        const titreDuResultat = lireTitreDuResultat(
            etude,
            ctx.sans ?? null,
            ctx.avec ?? null
        )
        if (titreDuResultat.forme === "economie_possible") {
            valeurs.push({
                terme: LIBELLE_ETAT.economie,
                valeur: `${montantEconomiePossible(titreDuResultat)} en assumant les dépassements`,
            })
        } else if (reserveC5EnTitre(etude)) {
            valeurs.push({
                terme: "Résultat",
                valeur: LIBELLE_COURT_RESERVE_C5,
            })
        } else {
            valeurs.push({
                terme: "Résultat",
                valeur: chiffre.etat ? LIBELLE_ETAT[chiffre.etat] : "Montant",
            })
        }
    }
    if (ctx.arbitragePertinent && ctx.retenu && !nomDejaDit) {
        // Arbitrage du pilote du 15/10/2026 (question 3.6) : la barre écrit
        // « (recommandé) » après le scénario affiché quand c'est le conseil
        // ÉTABLI, comme la carte de tête (E6).
        valeurs.push({
            terme: "Scénario affiché",
            valeur: `${nomScenario(ctx.retenu)}${
                ctx.conseil === ctx.retenu ? " (recommandé)" : ""
            }`,
        })
    }
    const date = dateEtude(etude, ctx)
    if (date) valeurs.push({ terme: "Étude", valeur: date, secondaire: true })
    return valeurs
}

// -----------------------------------------------------------------------------
// La ligne du cadre (toujours visible)
// -----------------------------------------------------------------------------

export type ElementDuCadre = {
    terme: string
    valeur: string
    /** Un fait qui précise la valeur, écrit à côté (jamais en bannière). */
    note?: string | null
    /**
     * Une réserve attachée à la valeur, en pastille : les valeurs prises par
     * défaut, NOMMÉES (« à confirmer : option pointe fixe »), jamais un simple compte (constat de lecture du 07/10).
     */
    pastille?: string | null
    /**
     * Les mesures qui s'arrêtent bien avant le calcul (D14, fait 7) : dit
     * seulement en alerte, ou quand le moteur a établi l'arrêt de la collecte
     * (décision (n) du 20/10/2026, toujours en gris). Le « dernier jour
     * mesuré » ordinaire est la fin de la période, il ne s'écrit plus.
     */
    mesures?: { texte: string; alerte: boolean } | null
    /** Valeur par défaut de l'étude : elle se tait sous 640 px. */
    secondaire?: boolean
}

/** « option pointe fixe » : une valeur par défaut nommée (la date du barème n'en est plus une, R5). */
function nomDeLaValeurParDefaut(entree: EntreeLue): string | null {
    // Jamais la valeur brute (0.05) : la valeur formatée de la ligne du registre.
    const brute = texteValeurRetenue(entree.valeur)
    // (le texte des valeurs à code, comme l'option tarifaire, reste traduit).
    const valeur =
        brute && typeof entree.valeur !== "string"
            ? formaterValeurEntree(entree.valeur, entree.unite, formatNombreFr)
            : brute
    if (entree.champ === CHAMP_DATE_BAREME) {
        const date = formatDateFr(
            typeof entree.valeur === "string" ? entree.valeur : null
        )
        return date ? `barème du ${date}` : "date du barème"
    }
    if (entree.champ === "structure_tarifaire.offre_tarifaire.option_tarifaire")
        return valeur ? `option ${valeur}` : "pointe fixe ou pointe mobile"
    // Un champ que la page ne sait pas nommer n'est jamais écrit par son
    // chemin technique : il reste compté, pas nommé.
    if (entree.libelle === entree.champ) return null
    const libelle =
        entree.libelle.charAt(0).toLowerCase() + entree.libelle.slice(1)
    return valeur ? `${libelle} ${valeur}` : libelle
}

/**
 * Les valeurs prises par défaut qui pèsent sur le résultat, nommées : ce que
 * le client doit vérifier dans son contrat avant d'agir. Une valeur sans effet
 * sur l'étude (« TURPE inclus, sans effet ») n'est pas à confirmer.
 */
export function valeursParDefautNommees(
    etude: OptimTurpeV2Etude | null | undefined
): string[] {
    if (!etude) return []
    return (lireRegistre(etude) ?? [])
        .filter((e) => e.estAuDefaut && e.peseSurLeResultat)
        .map(nomDeLaValeurParDefaut)
        .filter((n): n is string => Boolean(n))
}

/** Un réglage de l'étude (marge, plancher, talon, scénario, objectif), pas une valeur du contrat. */
function estUnReglageDeLEtude(champ: string): boolean {
    return champ.startsWith("optimisation.")
}

/**
 * La ligne des hypothèses de l'étude (vague wording du 14/10/2026, Q8, Q17,
 * Q20, Q41) : la période et l'arrêt des mesures quand il est ancien, le
 * contrat lu chez Enedis, la marge de sécurité (15 % d'office en C5, Q10),
 * l'objectif de l'étude, la grille TURPE appliquée, et tout réglage d'expert
 * posé. La période ne s'écrit qu'ici (zéro redite).
 */
export function ligneDuCadre(
    etude: OptimTurpeV2Etude | null | undefined
): ElementDuCadre[] {
    if (!etude) return []
    const elements: ElementDuCadre[] = []
    const periode = lireChiffre(etude).periode
    const du = formatDateFr(periode.dateFrom)
    const au = formatDateFr(periode.dateTo)
    const duree = libelleDureeFenetre(etude)
    const mesures = noteDernierJourMesure(etude)
    // Contrat v1.6 §6 5.2, décision (s) : la baisse de puissance qui ouvre la
    // période se dit sur la ligne « Période », avec le libellé servi, en gris.
    const constatsDeLaPeriode = constatsDeLaLigne(etude, "periode")
    if (du && au) {
        elements.push({
            terme: "Période",
            valeur: [`${du} → ${au}`, duree].filter(Boolean).join(" · "),
            note:
                constatsDeLaPeriode.length > 0
                    ? constatsDeLaPeriode.join(" · ")
                    : null,
            // Décision (n) du 20/10/2026 : l'arrêt établi de la collecte se
            // dit toujours, en gris, même quand il tombe à la fin de la période.
            mesures:
                mesures && (mesures.alerte || mesures.arretDepuis)
                    ? { texte: mesures.texte, alerte: mesures.alerte }
                    : null,
        })
    } else {
        if (constatsDeLaPeriode.length > 0)
            elements.push({
                terme: "Période",
                valeur: constatsDeLaPeriode.join(" · "),
            })
        if (mesures)
            elements.push({
                terme: "Mesures",
                valeur: mesures.texte,
                mesures: null,
            })
    }

    // Décision (t) : une souscrite servie à 0 est un fait du contrat, dit en
    // gris sur la ligne du contrat actuel, avec le libellé servi.
    const constatsDuContrat = constatsDeLaLigne(etude, "contrat")
    const registre = lireRegistre(etude)
    if (!registre && constatsDuContrat.length > 0) {
        elements.push({
            terme: "Contrat d'acheminement",
            valeur: constatsDuContrat.join(" · "),
        })
    }
    if (registre) {
        // Une valeur dérivée (le segment) compte « lue chez Enedis » seulement
        // quand la formule dont elle se déduit l'est (`provenanceSource`).
        const lues = registre.filter(
            (e) => e.provenanceSource === "enedis"
        ).length
        // Revue témoin du 15/10/2026 (D11) : le compteur du contrat ne compte
        // que les valeurs du contrat. La marge, le plancher et le talon sont
        // des réglages de l'étude (`optimisation.*`) : une marge saisie se dit
        // sur la ligne de la marge.
        const corrigees = registre.filter(
            (e) => e.estSurchargee && !estUnReglageDeLEtude(e.champ)
        ).length
        // Contrat v1.2 §6 5.2 et 5.5 : la valeur se lit sur sa ligne avec sa
        // source ; le raccordement non retenu et les valeurs par défaut y
        // sont dits en mots, plus d'étiquette « à vérifier » (C18, C22).
        const raccordement = lireRaccordement(etude)
        const v =
            raccordement?.valeur !== null && raccordement?.valeur !== undefined
                ? formatPuissanceFr(
                      raccordement.valeur,
                      raccordement.unite ?? getUnitePuissance(etude)
                  )
                : null
        const parDefaut = valeursParDefautNommees(etude)
        elements.push({
            terme: "Contrat d'acheminement",
            valeur: [
                lues > 0
                    ? "formule et puissances lues chez Enedis"
                    : "non lu chez Enedis",
                raccordement?.statut === "ecartee"
                    ? v
                        ? `raccordement ${v} selon Enedis, non retenu`
                        : "raccordement non retenu"
                    : raccordement?.statut === "levee"
                      ? "puissance supérieure au raccordement autorisée"
                      : null,
            ]
                .filter(Boolean)
                .join(" · "),
            note:
                [
                    corrigees > 0
                        ? pluriel(
                              corrigees,
                              "valeur saisie par vous",
                              "valeurs saisies par vous"
                          )
                        : null,
                    parDefaut.length > 0
                        ? `${enumerer(parDefaut)} par défaut`
                        : null,
                    ...constatsDuContrat,
                ]
                    .filter(Boolean)
                    .join(" · ") || null,
        })
    }

    // Q10 : en C5, le moteur applique sa marge d'office, quelle que soit la
    // marge envoyée ; la ligne dit la marge SERVIE (`pmax_mesuree`), et se
    // tait quand le moteur ne la sert pas.
    const marge = fini(etude.inputs?.optimisation?.marge_securite_pct)
    const margeSaisie =
        registre?.some(
            (e) => e.champ === CHAMP_MARGE_SECURITE && e.estSurchargee
        ) ?? false
    if (etude.compteur?.segment === "C5") {
        const margeC5 = libelleMargeC5Servie(etude.pmax_mesuree)
        if (margeC5 !== null) {
            elements.push({
                terme: "Marge de sécurité",
                valeur: margeC5,
                secondaire: true,
            })
        }
    } else if (marge !== null) {
        const pct = marge <= 1 ? Math.round(marge * 100) : Math.round(marge)
        elements.push({
            terme: "Marge de sécurité",
            valeur: libelleMarge(pct),
            note: margeSaisie
                ? "saisie par vous"
                : sourceMargeParDefaut(etude, pct),
            secondaire: !margeSaisie,
        })
    }

    const saisi = lireChiffre(etude).estScenarioLibre
    elements.push({
        terme: "Objectif",
        valeur: saisi
            ? "le coût des puissances de votre choix"
            : "la combinaison la moins chère",
        secondaire: !saisi,
    })

    // Q41 : la grille du calcul se lit ici, elle ne se choisit plus.
    const grille = formatDateFr(
        etude.calcul?.bareme?.date_retenue ??
            etude.calcul?.bareme?.date_demandee
    )
    if (grille) {
        elements.push({
            terme: "Grille TURPE",
            valeur: `barème du ${grille}`,
            secondaire: true,
        })
    }

    return elements
}

// -----------------------------------------------------------------------------
// Rang 2 des paramètres : « Valeurs utilisées » (maquette allégée ; schéma de
// Mélodie du 13/10/2026)
// -----------------------------------------------------------------------------

const LIBELLE_CALCUL = "Valeurs utilisées"

/**
 * La source d'une valeur, en quatre mots (vague wording du 14/10/2026, partie
 * 4) : Enedis, Mesure du compteur, Votre saisie, Valeur par défaut. Le segment
 * déduit de la formule prend la source de cette formule (`provenanceSource`).
 * Une provenance inconnue de la page n'est rangée nulle part : elle reste
 * comptée au total, « non précisée ».
 */
function sourceDeLEntree(
    e: EntreeLue
): "enedis" | "mesure" | "saisie" | "defaut" | "inconnue" {
    if (e.estSurchargee) return "saisie"
    switch (e.provenanceSource) {
        case "enedis":
            return "enedis"
        case "mesure":
            return "mesure"
        case "surcharge_client":
            return "saisie"
        case "defaut_moteur":
        case "defaut_page":
            return "defaut"
        default:
            return "inconnue"
    }
}

/**
 * « Valeurs utilisées · 8 valeurs : 4 d'Enedis, 1 mesure du compteur, 2 par
 * défaut, 1 saisie par vous [à vérifier : …] ». A2 (14/10/2026) : chaque
 * valeur du registre tombe dans une seule source, et le total égale la somme
 * du détail (« 8 valeurs » pour 7 détaillées, R10). Sans registre, le noyau
 * se tait (jamais « 0 valeur »).
 */
export function enteteCalcul(
    etude: OptimTurpeV2Etude | null | undefined
): Entete {
    if (!etude) return absente(LIBELLE_CALCUL)
    const registre = lireRegistre(etude)
    if (!registre || registre.length === 0) {
        return lireStructure(etude.actuelle?.structure_tarifaire)
            ? entete(LIBELLE_CALCUL, null)
            : entete(LIBELLE_CALCUL, "contrat non lu chez Enedis")
    }
    const compte = (source: ReturnType<typeof sourceDeLEntree>) =>
        registre.filter((e) => sourceDeLEntree(e) === source).length
    const n = (v: number) => formatNombreFr(v, 0)
    const parts = [
        compte("enedis") > 0 ? `${n(compte("enedis"))} d'Enedis` : null,
        compte("mesure") > 0
            ? `${n(compte("mesure"))} ${compte("mesure") > 1 ? "mesures" : "mesure"} du compteur`
            : null,
        compte("defaut") > 0 ? `${n(compte("defaut"))} par défaut` : null,
        compte("saisie") > 0
            ? `${n(compte("saisie"))} ${compte("saisie") > 1 ? "saisies" : "saisie"} par vous`
            : null,
        compte("inconnue") > 0
            ? `${n(compte("inconnue"))} de source non précisée`
            : null,
    ].filter((p): p is string => Boolean(p))
    return entete(
        LIBELLE_CALCUL,
        `${pluriel(registre.length, "valeur")}${parts.length > 0 ? ` : ${parts.join(", ")}` : ""}`,
        null,
        pastilleDuCalcul(etude),
        "repliee",
        true
    )
}

// -----------------------------------------------------------------------------
// Rang 2 du temps 1 : « Poste par poste »
// -----------------------------------------------------------------------------

/**
 * Ce qui fait le montant, en une phrase (constat de lecture du 07/10) : la
 * part fixe et la consommation vont en sens contraires. Lue sur les deux
 * postes servis ; dans tout autre cas, rien (la page ne devine pas).
 */
export function phraseDuMontant(
    etude: OptimTurpeV2Etude | null | undefined
): string | null {
    const postes = etude?.economie?.reconciliation?.postes ?? []
    const economie = (code: string) =>
        fini(postes.find((p) => p.poste === code)?.economie_eur)
    const fixe = economie("turpeCSF")
    const conso = economie("turpeCSV")
    if (fixe === null || conso === null) return null
    if (fixe > 0 && conso < 0)
        return "La recommandation réduit la part puissance (CSF), mais augmente la part énergie (CSV)."
    if (fixe < 0 && conso > 0)
        return "La recommandation augmente la part puissance (CSF), mais réduit la part énergie (CSV)."
    return null
}

/** Un terme de la composition du montant, signé (Q14). */
export type TermeDeComposition = {
    /** « part puissance (CSF) », « dépassements évités »… */
    nom: string
    eur: number
}

/**
 * La composition du montant en quatre termes, chacun servi par le moteur
 * (vague wording du 14/10/2026, Q14, Q17, A13 : l'écran comme le PDF) : part
 * puissance (CSF), part énergie (CSV), CTA, puis les dépassements évités
 * (`economie.depassements_eur`), « ajoutés » quand ils coûtent. Signes
 * comptables : « + » le client économise, « − » il paie plus. Un terme non
 * servi n'est pas écrit (jamais un 0 inventé).
 *
 * La réconciliation servie compte six postes (courtage `restitution.py`,
 * `POSTES_STRUCTURE_FERME`) : les trois autres (énergie réactive CER,
 * comptage CC, gestion CG) s'écrivent en un terme « autres composantes »
 * quand leur somme ne s'arrondit pas à zéro, pour que les termes somment au
 * montant de tête (revue de nuit du 15/10/2026, E11).
 */
export function compositionDuMontant(
    etude: OptimTurpeV2Etude | null | undefined
): TermeDeComposition[] {
    if (!etude) return []
    const postes = etude.economie?.reconciliation?.postes ?? []
    const economie = (code: string) =>
        fini(postes.find((p) => p.poste === code)?.economie_eur)
    // P2 de Mélodie : les dépassements évités d'abord, puis les parts du
    // tarif.
    const termes: TermeDeComposition[] = []
    const depassements = lireChiffre(etude).decomposition.depassementsEur
    if (depassements !== null && Math.round(depassements) !== 0) {
        termes.push({
            nom:
                depassements > 0
                    ? "dépassements évités"
                    : "dépassements ajoutés",
            eur: depassements,
        })
    }
    const fixe = economie("turpeCSF")
    const conso = economie("turpeCSV")
    const cta = economie("cta")
    if (fixe !== null) termes.push({ nom: "part puissance (CSF)", eur: fixe })
    if (conso !== null) termes.push({ nom: "part énergie (CSV)", eur: conso })
    if (cta !== null) termes.push({ nom: "CTA", eur: cta })
    const autres = ["turpeCER", "turpeCC", "turpeCG"]
        .map(economie)
        .filter((v): v is number => v !== null)
    const sommeAutres = autres.reduce((a, b) => a + b, 0)
    if (autres.length > 0 && Math.round(sommeAutres) !== 0)
        termes.push({ nom: AUTRES_COMPOSANTES, eur: sommeAutres })
    return termes
}

/** Le terme des postes CER, CC et CG réunis (E11). */
export const AUTRES_COMPOSANTES = "autres composantes"

/**
 * La raison du montant, en euros (Q18) : le terme qui pèse le plus dans
 * l'économie, « L'essentiel vient de dépassements évités (2 477 €). ».
 * `null` quand aucun terme ne fait gagner : la page ne devine pas.
 */
export function raisonDuMontant(
    etude: OptimTurpeV2Etude | null | undefined
): string | null {
    const termes = compositionDuMontant(etude).filter(
        (t) => Math.round(t.eur) > 0
    )
    if (termes.length === 0) return null
    const principal = termes.reduce((a, b) => (b.eur > a.eur ? b : a))
    const origine =
        principal.nom === "dépassements évités"
            ? "de dépassements évités"
            : principal.nom === AUTRES_COMPOSANTES
              ? `des ${AUTRES_COMPOSANTES}`
              : `de la ${principal.nom}`
    // Q14 : le montant de la raison se lit signé, comme partout ; E13 de la
    // revue de nuit : pas de seconde parenthèse après le sigle.
    return `L'essentiel vient ${origine} : ${formatMontantSigne(principal.eur, "HT")}.`
}

export function entetePostes(
    etude: OptimTurpeV2Etude | null | undefined,
    etat: EtatParcours,
    {
        compositionDansLaCarte = false,
    }: {
        /**
         * Série UX du témoin (15/10/2026, 2.3 point 4) : la carte de tête dit
         * déjà la composition du montant ; l'en-tête porteur ne la redit pas
         * (principe 5). Le libellé reste (U4 : pas de renommage).
         */
        compositionDansLaCarte?: boolean
    } = {}
): Entete {
    const libelle =
        etat === "deja_optimale"
            ? "D'où vient cet écart"
            : "D'où vient ce montant"
    if (!etude || etat === "refus" || etat === "montant_non_servi") {
        return absente(libelle)
    }
    // C5 sous le pic (contrat v1.3 §1.1, US12 CA1 et CA4, règle du montant
    // C5) : la réserve se lit avant l'état. La composition servie est celle de
    // la combinaison retenue par l'optimisation, pas de la puissance adaptée à
    // formule actuelle, et le chiffrage dédié n'a pas de décomposition :
    // « Montant par composante » se tait (en-tête et tableau), le montant se
    // dit sous « D'où vient le montant » de la carte (relecture sceptique du
    // lot, 19/10 : les 46, 19 et 7 € sommaient à 72 € sous une raison à 62 €).
    // Le prédicat est celui de la carte (`reserveC5EnTitre`) : sur une vraie
    // économie, la réserve ne tient pas lieu de titre et la composition de
    // l'économie se dit (relecture sceptique 2, 19/10).
    if (etat !== "scenario_saisi" && reserveC5EnTitre(etude)) {
        return absente(libelle)
    }
    // Revue du 08/10 (R1), puis Q14 du 14/10/2026 : chaque terme se lit
    // signé, à la convention de toute la page, et les quatre termes de la
    // composition somment au montant de tête (A13).
    const termes = compositionDuMontant(etude)
    const ferme = lireChiffre(etude).decomposition.fermeEur
    let noyau: string | null
    if (termes.length > 0 && compositionDansLaCarte) {
        noyau = null
    } else if (termes.length > 0) {
        // Budget de l'en-tête de rang 2 : les sigles restent dans le détail.
        noyau = termes
            .map(
                (t) =>
                    `${t.nom.replace(/ \((CSF|CSV)\)$/, "")} ${eurosCourts(t.eur)}`
            )
            .join(", ")
    } else {
        noyau =
            ferme !== null
                ? `formule et puissances ${eurosCourts(ferme)}`
                : null
    }
    const pastille = pastilleDeReserve(etude, "postes")
    return entete(libelle, noyau, null, pastille, "repliee", true)
}

// -----------------------------------------------------------------------------
// Rang 1 : la section « Dépassements » (ex-temps 2 « Choisir ») ; vague wording
// du 14/10/2026 (W17, W48, W54, A8).
// -----------------------------------------------------------------------------

const LIBELLE_T2 = "Dépassements"

/**
 * Ce que la section affiche : libellé replié, titre et question dépliés, et
 * son étape dans le fil.
 */
export type PresentationDuTemps2 = {
    libelle: string
    titre: string
    question: string
    fil: string
}

/** Pourquoi il n'y a qu'un scénario, selon ce que l'étude servie établit. */
export const RAISON_SCENARIO_UNIQUE = {
    segment:
        "Pas de dépassement facturé sur un compteur C5 (jusqu'à 36 kVA) : une seule recommandation.",
    sansDepassement:
        "Les puissances recommandées ne laissent aucun dépassement à assumer.",
    saisi: "Vos puissances, chiffrées telles quelles.",
    seulChiffre: "L'autre scénario n'est pas chiffré pour cette étude.",
} as const

/** La question du marché, sous le titre de la section (W48). */
export const QUESTION_DES_DEPASSEMENTS =
    "Payer quelques dépassements ou augmenter la puissance ?"

/** La question de la section quand aucun dépassement n'est attendu (arbitrage 3.5). */
export const QUESTION_SANS_DEPASSEMENT =
    "Garder une marge de sécurité ou souscrire au plus juste ?"

/**
 * Aucun dépassement n'est-il ÉTABLI sur la paire ? La règle du conseil servi,
 * sinon celle du repli sur les montants, et la durée servie : les mêmes faits
 * que la carte « Assumer les dépassements » (`aucunDepassementEtabli`).
 */
function aucunDepassementDeLaPaire(ctx: ContexteEntetes): boolean {
    const sans = ctx.sans ?? null
    const avec = ctx.avec ?? null
    if (!sans || !avec) return false
    const servi = lireArbitrageServi(sans, avec)
    const regle = servi?.conseil
        ? servi.regle
        : conseilDeRepli(sans, avec).regle
    return aucunDepassementEtabli(regle, servi?.dureeDepassementH)
}

/**
 * La paire montre-t-elle qu'il n'y a aucun dépassement à assumer ? Les deux
 * scénarios servis aboutissent à la même part formule et puissances, ou le
 * moteur sert zéro dépassement assumé. Sans ces faits, la page ne le dit pas.
 */
function aucunDepassementAAssumer(ctx: ContexteEntetes): boolean {
    const arbitrage = lireArbitrageServi(ctx.sans ?? null, ctx.avec ?? null)
    if (arbitrage?.depassementsAssumes === 0) return true
    if (arbitrage?.dureeDepassementH === 0) return true
    const ferme = (s: ScenarioPaire): number | null => {
        const leg = s === "sans" ? ctx.sans : ctx.avec
        return (
            arbitrage?.montants[s].ferme ??
            (leg ? lireChiffre(leg).decomposition.fermeEur : null)
        )
    }
    const sans = ferme("sans")
    const avec = ferme("avec")
    if (sans === null || avec === null || sans !== avec) return false
    // Des montants égaux ne suffisent pas : même formule et mêmes puissances.
    const sSans = lireStructure(ctx.sans?.meilleure?.structure_tarifaire)
    const sAvec = lireStructure(ctx.avec?.meilleure?.structure_tarifaire)
    if (!sSans || !sAvec) return false
    if (sSans.fta !== sAvec.fta || sSans.unique !== sAvec.unique) return false
    return PLAGE_ORDER.every((c) => sSans.puissances[c] === sAvec.puissances[c])
}

/**
 * Le verdict de la carte est-il « Rien à changer sur l'acheminement » ? Les
 * mêmes faits que la carte de tête : un état déjà optimal ou en surcoût, sans
 * économie possible en assumant les dépassements (D27) ni réserve C5 portée
 * en titre (Q22).
 */
function verdictRienAChanger(ctx: ContexteEntetes): boolean {
    const etude = ctx.etude
    if (!etude) return false
    const etat = etatDuParcours(ctx)
    if (etat !== "deja_optimale" && etat !== "surcout") return false
    // Le scénario affiché n'est pas la recommandation : la carte dit « Ce
    // n'est pas la recommandation » (A7), la rangée ne dit pas « aucune
    // démarche » (relecture sceptique de la revue 2).
    if (estScenarioNonRecommande(ctx)) return false
    if (reserveC5EnTitre(etude)) return false
    return (
        lireTitreDuResultat(etude, ctx.sans ?? null, ctx.avec ?? null).forme !==
        "economie_possible"
    )
}

/**
 * Le libellé, le titre et la question de la section « Dépassements ».
 * Décision (r) du 20/10/2026, qui applique D2 (a) du 18/10 et le contrat §6
 * 2.1 : le titre est FIXE, « Dépassements », dans tous les états ; il ne
 * compose aucune conclusion ni aucun écart (le verdict se dit une fois,
 * R-T13). Les montants vont au résumé de la rangée (`noyauDepassements`).
 * La question dit pourquoi il n'y a qu'un scénario, sans deviner une raison
 * que l'étude ne sert pas.
 */
export function presentationDuTemps2(
    ctx: ContexteEntetes
): PresentationDuTemps2 {
    // Revue témoin du 15/10/2026 (D6, arbitrage 3.5) : quand aucun
    // dépassement n'est ÉTABLI (même fait que la carte « Assumer les
    // dépassements » : règle `aucun-depassement` ou durée servie nulle), la
    // question ne parle pas de payer des dépassements : l'écart entre les
    // deux scénarios tient à la marge de sécurité.
    const aucunAttendu = aucunDepassementDeLaPaire(ctx)
    const choisir: PresentationDuTemps2 = {
        libelle: LIBELLE_T2,
        titre: LIBELLE_T2,
        question: aucunAttendu
            ? QUESTION_SANS_DEPASSEMENT
            : QUESTION_DES_DEPASSEMENTS,
        fil: LIBELLE_T2,
    }
    const etat = etatDuParcours(ctx)
    if (etat === "vide" || etat === "refus" || etat === "montant_non_servi")
        return choisir
    const etude = ctx.etude
    if (!etude) return choisir
    const unique = (question: string): PresentationDuTemps2 => ({
        libelle: LIBELLE_T2,
        titre: LIBELLE_T2,
        question,
        fil: LIBELLE_T2,
    })
    if (etat === "scenario_saisi") return unique(RAISON_SCENARIO_UNIQUE.saisi)
    if (!cmdpsApplicable(etude)) return unique(RAISON_SCENARIO_UNIQUE.segment)
    if (ctx.arbitragePertinent && ctx.retenu) return choisir
    return unique(
        aucunDepassementAAssumer(ctx)
            ? RAISON_SCENARIO_UNIQUE.sansDepassement
            : RAISON_SCENARIO_UNIQUE.seulChiffre
    )
}

/**
 * L'en-tête de la section « Dépassements ». A8 (14/10/2026) : une section
 * sans contenu est retirée (refus, économie non chiffrée, compteur C5, où
 * aucun dépassement n'est facturé : W54).
 */
export function enteteChoisir(ctx: ContexteEntetes): Entete {
    const etat = etatDuParcours(ctx)
    const etude = ctx.etude
    if (etat === "vide" || etat === "refus" || etat === "montant_non_servi")
        return absente(LIBELLE_T2)
    if (!etude) return absente(LIBELLE_T2)
    if (!cmdpsApplicable(etude)) return absente(LIBELLE_T2)
    const libelle = presentationDuTemps2(ctx).libelle
    if (etat === "scenario_saisi") {
        const ferme = lireChiffre(etude).decomposition.fermeEur
        return entete(
            libelle,
            "Vos puissances, chiffrées telles quelles",
            ferme !== null
                ? `formule et puissances ${eurosSignes(ferme)}`
                : null
        )
    }
    const arbitrage = lireArbitrageServi(ctx.sans ?? null, ctx.avec ?? null)
    if (etat === "deja_optimale") {
        return entete(libelle, "Le contrat actuel est conservé")
    }
    if (etat === "surcout") {
        const couvrir = arbitrage?.montants.sans.nette ?? null
        const assumer = arbitrage?.montants.avec.nette ?? null
        const complement = [
            couvrir !== null ? `couvrir : ${eurosSignes(couvrir)}` : null,
            assumer !== null ? `assumer : ${eurosSignes(assumer)}` : null,
        ]
            .filter(Boolean)
            .join(" · ")
        return entete(
            libelle,
            "Aucun changement recommandé",
            complement || null
        )
    }
    if (!ctx.arbitragePertinent || !ctx.retenu) {
        // Un seul scénario chiffré, ou deux qui ne diffèrent pas sur la part
        // ferme : la section nomme le scénario affiché et son montant.
        const allow = etude.inputs?.optimisation?.allow_cmdps
        const nom =
            allow === true
                ? nomScenario("avec")
                : allow === false
                  ? nomScenario("sans")
                  : null
        return entete(
            libelle,
            nom,
            eurosSignes(lireChiffre(etude).netteEur),
            pastilleDeReserve(etude, "t2") ?? pastilleDuRaccordement(etude),
            "repliee",
            true
        )
    }
    // Les deux scénarios diffèrent : le choix est la section.
    const retenu = ctx.retenu
    const autre: ScenarioPaire = retenu === "sans" ? "avec" : "sans"
    const conseille = (s: ScenarioPaire) => ctx.conseil === s
    const montant = (s: ScenarioPaire): string | null => {
        const leg = s === "sans" ? ctx.sans : ctx.avec
        const nette =
            arbitrage?.montants[s].nette ??
            (leg ? lireChiffre(leg).netteEur : null)
        const ferme =
            arbitrage?.montants[s].ferme ??
            (leg ? lireChiffre(leg).decomposition.fermeEur : null)
        const parts = [eurosSignes(nette)]
        if (s === "avec") {
            if (ferme !== null)
                parts[0] = `${parts[0]} dont formule et puissances ${eurosCourts(ferme)}`
            // Q18 : une seule durée de dépassement, celle du scénario servie
            // par le moteur ; « forts appels » n'est plus écrit.
            const duree = arbitrage?.dureeDepassementH ?? null
            const estimes = arbitrage?.depassementsAssumes ?? null
            const risque = [
                duree !== null ? `${formatNombreFr(duree, 1)} h` : null,
                estimes !== null
                    ? `${nombreEurosDepassement(estimes)}\u00a0€\u00a0HT de dépassements estimés`
                    : null,
            ]
                .filter(Boolean)
                .join(", ")
            if (risque) parts.push(risque)
        }
        return parts.filter(Boolean).join(" · ") || null
    }
    const motAutre = autre === "sans" ? "couvrir" : "assumer"
    const noyau = `${nomScenario(retenu)}${conseille(retenu) ? ", recommandé" : ""}`
    const complement = [
        montant(retenu),
        `${motAutre}${conseille(autre) ? ", recommandé" : ""} : ${montant(autre) ?? "montant non chiffré"}`,
    ]
        .filter(Boolean)
        .join(" · ")
    return entete(
        LIBELLE_T2,
        noyau,
        complement,
        pastilleDeReserve(etude, "t2") ?? pastilleDuRaccordement(etude),
        "repliee",
        true
    )
}

// -----------------------------------------------------------------------------
// Rang 1 : « Rapport et demande » (Q36 du 14/10/2026 : les anciens temps 3
// « Partager » et 4 « Agir et suivre », regroupés en une section)
// -----------------------------------------------------------------------------

const LIBELLE_T3 = "Rapport et demande"

/**
 * La pastille de « Valeurs utilisées » (revue du support du 08/10, A5) : elle
 * NOMME ce qui est à vérifier (Q11, « Points à vérifier »), comme la ligne des
 * hypothèses, quand chaque valeur se nomme (« à vérifier : option pointe fixe
 * et raccordement non retenu ») ; sinon elle compte. Mêmes valeurs que
 * `valeursAConfirmer`, qui reste le compte du rapport.
 */
export function pastilleDuCalcul(
    etude: OptimTurpeV2Etude | null | undefined
): string | null {
    const n = valeursAConfirmer(etude)
    if (!etude || n === 0) return null
    const noms = (lireRegistre(etude) ?? [])
        .filter((e) => e.estAuDefaut)
        .map(nomDeLaValeurParDefaut)
    if (lireRaccordement(etude)?.statut === "ecartee")
        noms.push("raccordement non retenu")
    const nommees = noms.filter((nom): nom is string => Boolean(nom))
    return nommees.length === n
        ? `à vérifier : ${enumerer(nommees)}`
        : `${formatNombreFr(n, 0)} à vérifier`
}

/**
 * Les valeurs « à confirmer » du rapport : les valeurs par défaut, un raccordement écarté.
 * La part des dépassements n'y entre pas : elle est estimée sur les relevés passés,
 * pas à confirmer par le client (retour de relecture du 07/10, lexique client).
 */
export function valeursAConfirmer(
    etude: OptimTurpeV2Etude | null | undefined
): number {
    if (!etude) return 0
    let n = 0
    n += (lireRegistre(etude) ?? []).filter((e) => e.estAuDefaut).length
    if (lireRaccordement(etude)?.statut === "ecartee") n += 1
    return n
}

/** « HTACU5, 416 et 483 kW » : ce qui change, formule d'abord. */
function aDemander(
    avant: Structure | null,
    apres: Structure,
    unite: string
): string | null {
    const puissances = puissancesDistinctes(apres, unite)
    const ftaChange = apres.fta !== null && apres.fta !== avant?.fta
    // Q2 : dans ce qui se demande au fournisseur, la formule garde son code
    // Enedis complet, après son libellé.
    return (
        [
            ftaChange && apres.fta ? libelleFormuleTarifaire(apres.fta) : null,
            puissances,
        ]
            .filter(Boolean)
            .join(", ") || null
    )
}

/**
 * L'en-tête de « Rapport et demande » (Q36, W63) : ce qui se demande au
 * fournisseur, ou pourquoi rien ne se demande, puis le rapport de l'étude.
 * Une section sans contenu (refus) est retirée (A8).
 */
export function entetePartager(ctx: ContexteEntetes): Entete {
    const etat = etatDuParcours(ctx)
    const etude = ctx.etude
    if (etat === "vide" || etat === "refus" || !etude)
        return absente(LIBELLE_T3)
    const date = dateEtude(etude, ctx)
    const jour = date ? date.slice(0, 10) : null
    const rapport = jour
        ? `rapport de l'étude du ${jour}`
        : "rapport de l'étude"
    const n = valeursAConfirmer(etude)
    const aVerifier =
        n > 0 ? `${pluriel(n, "valeur")} à vérifier au rapport` : null
    switch (etat) {
        case "deja_optimale":
            return entete(
                LIBELLE_T3,
                "Aucune démarche : rien à changer sur l'acheminement",
                rapport
            )
        case "surcout":
            return entete(
                LIBELLE_T3,
                "Aucune démarche recommandée",
                [rapport, aVerifier].filter(Boolean).join(" · ")
            )
        case "montant_non_servi":
            return entete(
                LIBELLE_T3,
                "Aucune valeur à demander tant que l'économie n'est pas chiffrée",
                "rapport non chiffré"
            )
        default:
            break
    }
    const avant = lireStructure(etude.actuelle?.structure_tarifaire)
    const apres = lireStructure(etude.meilleure?.structure_tarifaire)
    const demande = apres
        ? aDemander(avant, apres, getUnitePuissance(etude))
        : null
    const saisi = etat === "scenario_saisi"
    const nomRapport =
        !saisi && ctx.arbitragePertinent && ctx.retenu
            ? `rapport « ${nomScenario(ctx.retenu)} »${jour ? `, étude du ${jour}` : ""}`
            : rapport
    const resume = resumeDuRapport(etude) ? "résumé prêt à copier" : null
    const pastille = saisi
        ? "Ce n'est pas la recommandation"
        : pastilleDeReserve(etude, "t3")
    return entete(
        LIBELLE_T3,
        demande ? `à demander : ${demande}` : nomRapport,
        // Le nom du rapport ne se dit dans l'en-tête que sans demande (budget
        // des en-têtes de rang 1) ; il reste sur le bouton du rapport.
        [saisi ? "pour vos puissances" : null, aVerifier, resume]
            .filter(Boolean)
            .join(" · ") || null,
        pastille,
        "repliee",
        pastille !== null
    )
}

/**
 * L'ancien en-tête du temps 4 « Agir et suivre » : sa section est regroupée
 * avec le rapport (Q36), il n'a plus de place dans la page.
 */
export function enteteAgir(ctx: ContexteEntetes): Entete {
    void ctx
    return absente("Agir et suivre")
}

// -----------------------------------------------------------------------------
// Rang 2 de « Rapport et demande » : l'accompagnement (lot D, D16).
// -----------------------------------------------------------------------------

/**
 * Le texte rédigé servi avec l'étude (la conclusion), sinon rien. Q40 : le
 * résumé à copier se compose à partir des valeurs servies
 * (`optim-turpe-v2.carte.ts`) ; ce texte devient facultatif.
 */
export function resumeDuRapport(
    etude: OptimTurpeV2Etude | null | undefined
): string | null {
    const conclusion = etude?.explain?.conclusion
    return typeof conclusion === "string" && conclusion.trim() !== ""
        ? conclusion.trim()
        : null
}

// -----------------------------------------------------------------------------
// Rang 2 du temps 2 : « Pourquoi ces puissances »
// -----------------------------------------------------------------------------

export function entetePourquoi(
    etude: OptimTurpeV2Etude | null | undefined,
    etat: EtatParcours
): Entete {
    const c5 = etude ? !cmdpsApplicable(etude) : false
    const libelle = c5 ? "Pourquoi cette puissance" : "Pourquoi ces puissances"
    if (!etude || etat === "refus" || etat === "montant_non_servi") {
        return absente(libelle)
    }
    const unite = getUnitePuissance(etude)
    const pointes = etude.pointes_mesurees_kw ?? {}
    const plage = getDrivingPlage(etude)
    if (c5) {
        // C5 sous le pic (contrat v1.3 §3.1, F6, décision du 19/10 n° 1) : ce
        // que coûte la puissance qui lève le risque, quand le chiffrage dédié
        // est servi ; sinon le risque seul, jamais un montant emprunté.
        const risque = phraseRisqueC5(etude, etat)
        if (risque) return entete(libelle, risque)
        const max = Object.values(pointes).filter(
            (v): v is number => typeof v === "number"
        )
        return entete(
            libelle,
            max.length > 0
                ? `pic de ${formatPuissanceFr(Math.max(...max), unite, 1)}`
                : null
        )
    }
    // La puissance maximale atteinte du poste : servie à part, sinon le
    // maximum mesuré du diagnostic du poste, sinon celle du poste moteur.
    const pointe = plage
        ? (fini(pointes[plage]) ??
          fini(etude.diagnostic?.plages?.[plage]?.stats_kw?.max) ??
          (etude.plage_motrice?.classe === plage
              ? fini(etude.plage_motrice?.pointe_mesuree)
              : null))
        : null
    // Q6 : le poste en clair, avec son code.
    const classe = plage ? libelleClasseHoraire(plage) : null
    // Q18 (14/10/2026) : « forts appels » n'est plus écrit (une seule durée
    // de dépassement, celle du scénario). La forme de la pointe en pastille,
    // avec les mots de W57.
    const diagnostic = plage ? etude.diagnostic?.plages?.[plage] : undefined
    const forme = diagnostic?.driver?.type
    const pastille =
        forme === "pic_isole"
            ? "pic isolé"
            : forme === "comportement_normal"
              ? "pointe régulière"
              : forme === "saisonnier"
                ? "pointe saisonnière"
                : null
    // Démo du 20/10 (point 5) : quand la puissance du poste est celle du
    // plancher, et non celle de son pic, c'est le plancher qui est dit.
    const plancher = plage ? plancherQuiFixe(etude, plage, pointe) : null
    if (classe && plancher) {
        const valeur = formatPuissanceFr(plancher.valeur, unite)
        const source =
            plancher.provenance === "saisie"
                ? "plancher saisi"
                : "plancher du segment"
        return entete(libelle, `${classe} au ${source} : ${valeur}`)
    }
    const noyau =
        classe && pointe !== null
            ? `pic de ${formatPuissanceFr(pointe, unite, 1)} en ${classe}`
            : classe
    return entete(libelle, noyau || null, null, pastille)
}

/**
 * Le plancher qui FIXE la puissance du poste servi comme « motrice », ou
 * `null` quand c'est son pic (démo du 20/10, point 5).
 *
 * Le moteur sert comme poste moteur celui dont la puissance recommandée est
 * la plus haute (`restitution.plage_motrice`, lue sur
 * `diagnostic.synthese.plage_pilote`). Sur un C4, c'est souvent HCB relevé au
 * plancher du segment (37 kVA) bien au-dessus de son pic : écrire « pic de
 * 23,5 kVA en HCB » contredisait la tête de page. Le plancher fixe le poste
 * quand il le contraint (`plancher_modulation.retenu_classe`), que la
 * puissance recommandée du poste lui est égale et que le pic du poste reste
 * dessous.
 */
function plancherQuiFixe(
    etude: OptimTurpeV2Etude,
    plage: OptimTurpeV2PlageKey,
    pointe: number | null
): PlancherLu | null {
    const plancher = lirePlancherRetenu(etude)
    if (!plancher || plancher.provenance === null) return null
    const contrainte = etude.plancher_modulation?.retenu_classe
    if (contrainte && contrainte !== plage) return null
    const servie =
        etude.plage_motrice?.classe === plage
            ? fini(etude.plage_motrice?.puissance_recommandee)
            : null
    const recommandee =
        servie ?? fini(etude.diagnostic?.plages?.[plage]?.souscrit_propose_kva)
    if (recommandee === null) return null
    if (Math.round(recommandee) !== Math.round(plancher.valeur)) return null
    if (pointe !== null && pointe >= plancher.valeur) return null
    return plancher
}

/**
 * Le résumé de « Pourquoi cette puissance » sur un C5 sous le pic : « Passer
 * à 11 kVA coûte 62 € HT de plus par an. Sans ce changement, le compteur peut
 * disjoncter lors des prochains pics. » quand le montant du chiffrage dédié
 * est servi, « Sans passer à 11 kVA, le compteur peut disjoncter lors des
 * prochains pics. » sinon. `null` quand la réserve C5 ne tient pas lieu de
 * titre à la carte (économie, étude saisie) ou sans réserve.
 */
function phraseRisqueC5(
    etude: OptimTurpeV2Etude,
    etat: EtatParcours
): string | null {
    // Le prédicat de la carte : sous une économie, la tête ne dit pas le
    // risque, « Pourquoi cette puissance » non plus (relecture sceptique 2).
    if (etat === "scenario_saisi" || !reserveC5EnTitre(etude)) return null
    const montant = montantC5(etude)
    if (!montant) return null
    const kva = `${formatNombreFr(Math.round(montant.puissanceKva), 0)}\u00a0kVA`
    if (montant.surcoutEur === null)
        return `Sans passer à ${kva}, le compteur peut disjoncter lors des prochains pics.`
    return `Passer à ${kva} coûte ${montant.environ ? "environ " : ""}${nombreEuros(montant.surcoutEur)}\u00a0€ HT de plus par an. Sans ce changement, le compteur peut disjoncter lors des prochains pics.`
}

/**
 * « Le détail des deux scénarios · net +2 821 € ou +3 367 € » : les deux
 * nettes servies, dans l'ordre des cartes. Une nette non servie tait le noyau.
 */
export function enteteDetailScenarios(
    sans: number | null,
    avec: number | null
): Entete {
    const libelle = "Le détail des deux scénarios"
    if (sans === null || avec === null) return entete(libelle, null)
    return entete(libelle, `net ${eurosCourts(sans)} ou ${eurosCourts(avec)}`)
}

/** « Résumé pour votre client · deux phrases à copier ». */
export function enteteResume(texte: string | null): Entete {
    const libelle = "Résumé pour votre client"
    if (!texte) return absente(libelle)
    const phrases = texte
        .split(/(?<=[.!?])\s+/)
        .filter((p) => p.trim() !== "").length
    const mots: Record<number, string> = {
        1: "une phrase",
        2: "deux phrases",
        3: "trois phrases",
    }
    return entete(
        libelle,
        `${mots[phrases] ?? pluriel(phrases, "phrase")} à copier`
    )
}

/**
 * « Limites de l'étude » (Q11) : ce que la méthode ne couvre pas, distinct
 * des « Points à vérifier ». Repliée sous « À savoir avant d'envoyer ».
 */
export function enteteLimites(): Entete {
    // Revue 2 (I29) : un résumé court, deux lignes au plus à 375 px ; le
    // détail est dans la liste dépliée.
    return entete("Limites de l'étude", "ce que l'étude ne compte pas")
}

// -----------------------------------------------------------------------------
// Rang 2 du temps 2 : « L'explication » (lot E, partie « Quatre
// textes », texte 2). Le paragraphe « pourquoi » servi, mots inchangés.
// -----------------------------------------------------------------------------

/** Le paragraphe « pourquoi » servi avec l'étude, sinon rien. */
export function explicationRedigee(
    etude: OptimTurpeV2Etude | null | undefined
): string | null {
    const pourquoi = etude?.explain?.pourquoi
    return typeof pourquoi === "string" && pourquoi.trim() !== ""
        ? pourquoi.trim()
        : null
}

// -----------------------------------------------------------------------------
// Bloc à part, après les quatre temps : « Études de ce compteur » (R7)
// -----------------------------------------------------------------------------

export function enteteEtudes(
    etudes: OptimTurpeV2Etude[],
    couranteId: string | null,
    tronquee: boolean | null | undefined
): Entete {
    const libelle = "Études de ce compteur"
    const lancements = grouperParLancement(etudes)
    // Une liste vide sous une étude affichée (historique non lu) ne dit pas
    // « aucune étude » : le noyau se tait.
    if (lancements.length === 0)
        return entete(libelle, couranteId ? null : "aucune étude")
    const courant = couranteId
        ? lancements.find((l) => l.ids.includes(couranteId))
        : undefined
    // Contrat v1.2 §8.4.1 (lot 4d) : « 3 études, celle-ci est la
    // recommandation » ; étude saisie : « 3 études, celle-ci : Vos
    // puissances » ; une seule : « celle-ci seulement » ; « lancement » était
    // du jargon. On compte des LANCEMENTS (finitions 2 du 07/10/2026) : chacun
    // porte la paire des deux scénarios, qui ne compte pas pour deux. La
    // recommandation en vigueur est le lancement recommandé le plus récent
    // (les saisies ne remplacent rien, §8.4.10).
    //
    // DEV-2423 (§8.4.10, §8.4.11) : on compte les LIGNES de la liste (des
    // relances identiques font une ligne), et la recommandation en vigueur
    // suit la règle de la liste (`lancementEnVigueur`) : une seule, la plus
    // récente à avoir pris la main, calcul ou rétablissement.
    const lignes = lignesDHistorique(lancements, couranteId)
    const total = pluriel(lignes.length, "étude")
    const estSaisie = (l: (typeof lancements)[number]) =>
        lireChiffre(l.principale).estScenarioLibre
    const enVigueur = lancementEnVigueur(lancements)
    const noyau = !courant
        ? total
        : lignes.length === 1
          ? "celle-ci seulement"
          : estSaisie(courant)
            ? `${total}, celle-ci : Vos puissances`
            : courant === enVigueur
              ? `${total}, celle-ci est la recommandation`
              : `${total}, dont celle-ci`
    return entete(libelle, noyau, null, tronquee ? "liste partielle" : null)
}

// =============================================================================
// Maquette finale du 15/10/2026 (DEV-2404) : les résumés des huit rangées du
// sommaire. Chacun donne déjà la conclusion de sa rangée, en une ligne, à
// partir des seules valeurs servies ; une valeur absente tait le résumé
// (jamais un 0 inventé). Signes comptables (Q14), « par an » selon la période
// (Q15).
// =============================================================================

/** « 5 459 € HT » : un montant d'euros arrondi, sans signe. */
function eurosHT(valeur: number): string {
    return `${formatNombreFr(Math.round(valeur), 0)}\u00a0€\u00a0HT`
}

/** L'unité de la période de l'étude : « par an », ou « sur la période du … au … ». */
function uniteDeLaPeriode(etude: OptimTurpeV2Etude): string {
    const chiffre = lireChiffre(etude)
    return surLaPeriode(chiffre.periode.jours, chiffre.periode)
}

/**
 * « Montant par composante » : « Avant 5 459 € HT, après 5 387 € HT par an ».
 * Les totaux somment les composantes servies du tableau (postes et
 * dépassements estimés) ; une composante non servie tait le résumé, et une
 * étude sans réconciliation servie aussi (revue sceptique du 15/10/2026).
 */
export function noyauComposantes(
    etude: OptimTurpeV2Etude | null | undefined
): string | null {
    if (!etude) return null
    const decomposition = lireDecompositionTurpe(etude)
    // Étude ancienne sans réconciliation servie : la somme des composantes
    // historiques ne se réconcilie pas avec le titre de la carte (la CTA n'y
    // est pas) ; le résumé se tait plutôt que d'écrire un autre total.
    if (!decomposition.reconciliee) return null
    const lignes = decomposition.lignes.filter(
        (l) => l.nature === "poste" || l.nature === "hors-total"
    )
    if (lignes.length === 0) return null
    if (lignes.some((l) => l.avantEur === null || l.apresEur === null))
        return null
    const avant = lignes.reduce((a, l) => a + (l.avantEur ?? 0), 0)
    const apres = lignes.reduce((a, l) => a + (l.apresEur ?? 0), 0)
    return `Avant ${eurosHT(avant)}, après ${eurosHT(apres)} ${uniteDeLaPeriode(etude)}`
}

/**
 * « Dépassements » : « Assumer +72 € HT · Couvrir −101 € HT par an » (les deux
 * nettes servies de la paire, le scénario recommandé d'abord) ; sans paire,
 * le noyau de l'en-tête historique.
 */
export function noyauDepassements(ctx: ContexteEntetes): string | null {
    const etude = ctx.etude
    if (!etude) return null
    // Décision (r) du 20/10/2026 : sous « Rien à changer », le résumé commence
    // par « aucune démarche nécessaire », que le titre fixe ne porte plus,
    // puis les montants seuls (« Le contrat actuel est conservé » redirait le
    // verdict, R-T13).
    if (verdictRienAChanger(ctx)) {
        const montants = montantsDeLaPaire(ctx, etude)
        return montants
            ? `aucune démarche nécessaire · ${montants}`
            : "aucune démarche nécessaire"
    }
    if (ctx.arbitragePertinent) {
        const montants = montantsDeLaPaire(ctx, etude)
        if (montants !== undefined) return montants
    }
    return resumeDeLEntete(ctx, etude)
}

/**
 * Les montants nets servis de la paire, signés, le scénario recommandé
 * d'abord : « Assumer +X € HT · Couvrir +Y € HT par an ». `undefined` sans
 * arbitrage servi pour la paire ; `null` quand aucun montant n'est servi.
 */
function montantsDeLaPaire(
    ctx: ContexteEntetes,
    etude: OptimTurpeV2Etude
): string | null | undefined {
    const arbitrage = lireArbitrageServi(ctx.sans ?? null, ctx.avec ?? null)
    if (!arbitrage) return undefined
    const ordre: ScenarioPaire[] =
        ctx.conseil === "sans" ? ["sans", "avec"] : ["avec", "sans"]
    const parts = ordre
        .map((s) => {
            const nette = arbitrage.montants[s].nette
            return nette === null
                ? null
                : `${s === "avec" ? "Assumer" : "Couvrir"} ${eurosSignes(nette)}`
        })
        .filter((p): p is string => p !== null)
    if (parts.length === 0) return null
    return `${parts.join(" · ")} ${uniteDeLaPeriode(etude)}`
}

function resumeDeLEntete(
    ctx: ContexteEntetes,
    etude: OptimTurpeV2Etude
): string | null {
    const e = enteteChoisir(ctx)
    if (e.forme === "absente") return null
    const resume = [e.noyau, e.complement].filter(Boolean).join(" · ") || null
    // Rejeu du 20/10/2026 (A9) : un montant se dit avec sa durée, la même
    // règle que la carte de tête (« par an », ou « sur la période du … au … »
    // sous douze mois, décision A4) ; « Vos puissances … +1 936 € HT » ne la
    // disait pas. Le complément est la seule partie qui porte un montant.
    return resume && e.complement
        ? `${resume} ${uniteDeLaPeriode(etude)}`
        : resume
}

/**
 * « Pourquoi ces puissances » : « Pic de 67,83 kVA en heures pleines de
 * saison basse (HPB), pointe régulière » (le poste qui fixe la puissance, sa
 * puissance maximale atteinte servie, la forme de sa pointe).
 */
export function noyauPourquoi(
    etude: OptimTurpeV2Etude | null | undefined,
    etat: EtatParcours
): string | null {
    const e = entetePourquoi(etude, etat)
    if (e.forme === "absente" || !etude) return null
    // Q6 : le poste en clair, son code entre parenthèses.
    const noyau = e.noyau
        ? `${e.noyau.charAt(0).toUpperCase()}${e.noyau.slice(1)}`
        : null
    return [noyau, e.pastille].filter(Boolean).join(", ") || null
}

/**
 * « Hypothèses de l'étude » : « Du 14/10/2025 au 13/10/2026 · marge de
 * sécurité Standard (5 %) », lus sur la ligne des hypothèses. Décision (n) du
 * 20/10/2026 (contrat v1.6 §6 5.1) : quand le moteur a établi l'arrêt de la
 * collecte, « plus de mesures depuis le JJ/MM/AAAA » suit la période, en gris,
 * seule trace de l'arrêt au repos.
 */
export function noyauHypotheses(
    etude: OptimTurpeV2Etude | null | undefined
): string | null {
    const ligne = ligneDuCadre(etude)
    const periode = ligne.find((l) => l.terme === "Période")
    const dates = periode
        ? /^(\S+) → (\S+)/.exec(periode.valeur.replace(/\u00a0/g, " "))
        : null
    const marge = ligne.find((l) => l.terme === "Marge de sécurité")
    const arret = noteDernierJourMesure(etude)?.arretDepuis ?? null
    const parts = [
        dates ? `Du ${dates[1]} au ${dates[2]}` : null,
        arret ? `plus de mesures depuis le ${arret}` : null,
        marge
            ? `marge de sécurité ${marge.valeur.charAt(0).toUpperCase()}${marge.valeur.slice(1)}`
            : null,
    ].filter(Boolean)
    return parts.length > 0 ? parts.join(" · ") : null
}

/**
 * « Rapport et demande » (contrat v1.2 §7.4, C29) : avec démarche, « 5
 * puissances et la formule à reporter · demande à préparer · rapport · résumé
 * pour le client » ; sans démarche, « rapport · résumé pour le client » (le
 * verdict n'est pas redit, M9). Étude non chiffrée : le noyau historique.
 */
export function noyauRapport(
    ctx: ContexteEntetes,
    { resume = false }: { resume?: boolean } = {}
): string | null {
    const etude = ctx.etude
    if (!etude) return null
    const etat = etatDuParcours(ctx)
    if (etat === "refus" || etat === "vide") return null
    if (etat === "montant_non_servi") {
        const e = entetePartager(ctx)
        return e.forme === "absente" ? null : e.noyau
    }
    const demande = lireDemande(etude)
    const pourLeClient = resume ? "résumé pour le client" : null
    if (demande?.etat === "demande") {
        const changees = demande.lignes.filter(
            (l) => !l.reference && !l.inchangee
        )
        const puissances = changees.filter((l) =>
            l.libelle.startsWith("Puissance souscrite")
        ).length
        const formule = changees.some(
            (l) => l.libelle === "Formule tarifaire d'acheminement"
        )
        const aReporter = enumerer(
            [
                puissances > 0 ? pluriel(puissances, "puissance") : null,
                formule ? "la formule" : null,
            ].filter((p): p is string => p !== null)
        )
        return [
            aReporter ? `${aReporter} à reporter` : null,
            "demande à préparer",
            "rapport",
            pourLeClient,
        ]
            .filter(Boolean)
            .join(" · ")
    }
    return ["rapport", pourLeClient].filter(Boolean).join(" · ")
}
